import { Radio } from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import { isEmpty } from 'lodash'
import React from 'react'

import { apiEndpoints, apiURLOrigin } from 'api/common'
import { downloadJMDashBoard } from 'api/user'
import { DateRangePicker } from 'app/components/generic/DateRangePicker'
import constants from 'app/constants'
import { getJMName } from 'app/helpers'
import { formatAmount } from 'app/utils'
import { validateNotEmpty } from 'app/validators'

// import { MonthRangePicker } from './components/generic/MonthRangePicker'
export const publicNavigationPaths = {
  home: '/',
  login: '/login',
  notFound: '/not-found',
  schoolAttendance: '/school/attendance',
  // The parent's underage-consent page. PUBLIC on purpose: a parent has no
  // Alfred login and must not need one to give consent for their own child. The
  // token in the path is the authorisation — see PublicUnderageConsentAPI.
  underageConsent: '/consent/:token',
  // The new joiner's signed-offer upload page (HQ -> New Joiners). PUBLIC on
  // purpose: they have no Alfred login yet. The token in the path is the
  // authorisation — see PublicNewJoinerUploadAPI.
  newJoinerUpload: '/joining/:token',
  // The new joiner's joining-details form (Keka + ID card), from the welcome
  // email. Same rule: the token is the authorisation.
  joiningDetails: '/joining-details/:token',
  // The short forms Bella now puts in emails (2026-10-30); the long paths
  // above stay for links already sent.
  newJoinerUploadShort: '/j/:token',
  joiningDetailsShort: '/d/:token',
  // The centre's issue form. PUBLIC on purpose: it replaces a Google Form that
  // anyone at a centre could open, and not everyone who needs to raise an issue
  // has an Alfred login. The token in the path IS the centre — see
  // PublicIssueFormAPI — so the centre cannot be picked wrong, which it could be
  // on the Google Form's twelve-way dropdown.
  reportIssue: '/report-issue/:token',
  // Files a Centre Audit or Housekeeping Audit, in its own tab. The link fixes
  // the centre and the page needs a login, as the issue form does (2026-11-02).
  auditForm: '/audit-form/:kind/:centre',
  // The parent-filled alternative to Visitor Entry, reached by a per-centre QR
  // (?centre=<code> pre-fills the centre). PUBLIC on purpose: the parent has no
  // login — see PublicParentFormSubmitAPI for what guards the endpoint.
  newParentForm: '/new-parent-form'
}

export const mainLayoutNavigationPaths = {
  // Home (was /dashboard/*)
  parentdashboard: '/home/parent',
  teacherdashboard: '/home/teacher',

  // Audits (Preschool Daily Updates preview, 2026-07 - lives under Centre Audit)
  preschoolDailyUpdatesAudit: '/audits/preschool-daily-updates',
  teacherScorecard: '/audits/teacher-scorecard',
  centreVisitAudit: '/audits/centre-audit',
  hkAudit: '/audits/housekeeping-audit',

  // People (was /user/*)
  student: '/people/student',
  parent: '/people/parent',
  teacher: '/people/teacher',
  admin: '/people/admin',
  mergeRequests: '/people/merge-requests',

  // Classes (was /class/* and /attendance)
  batch: '/classes/batch',
  summerCampBatch: '/classes/summer-camp',
  enrolment: '/classes/enrolment',
  excelEnrolment: '/classes/excel-enrolment',
  psacEnrolment: '/classes/school-care-enrolment',
  activeCareEnrolment: '/classes/active-care-enrolment',
  toddlerClubEnrolment: '/classes/toddler-club-enrolment',
  exploreEnrolment: '/classes/explore-enrolment',
  trialsessions: '/classes/trial-sessions',
  toddlerClubSession: '/classes/toddler-club-sessions',
  exploreSession: '/classes/explore-sessions',
  makeUpSession: '/classes/makeup',
  trialEnrolment: '/classes/trial-enrolment',
  makeupEnrolment: '/classes/makeup-enrolment',
  bookMakeupSession: '/classes/makeup/book',
  attendance: '/classes/attendance/mark',

  // Programs (was /class/* + /journey/* + /portfolio + /community/club)
  subjectCategory: '/programs/subject-categories',
  subject: '/programs/subjects',
  playdateSubject: '/programs/subjects/playdate',
  toddlerClubSubject: '/programs/subjects/toddler-club',
  standard: '/programs/standards',
  course: '/programs/courses',

  // Preschool curriculum (Phase 5) — see
  // new-code/docs/weekly-skills-migration/phase-5-admin-portal/
  preschoolWeekBuilder: '/programs/preschool/week-builder',
  preschoolSkill: '/programs/preschool/skills',
  preschoolMonthlyTheme: '/programs/preschool/monthly-themes',
  preschoolAnnualLo: '/programs/preschool/annual-los',
  preschoolSpiralLo: '/programs/preschool/spiral-los',
  preschoolWeeklyEvaluation: '/programs/preschool/weekly-evaluation',
  preschoolWeeklyEvaluationView: '/programs/preschool/weekly-submissions',
  preschoolTermReport: '/programs/preschool/term-report',

  // Lesson Planner (EarlyLearn)
  plannerGradeGroups: '/programs/planner/grade-groups',
  plannerSubjects: '/programs/planner/subjects',
  plannerLayouts: '/programs/planner/layouts',
  plannerQuotas: '/programs/planner/quotas',
  plannerInventory: '/programs/planner/inventory',
  plannerRepository: '/programs/planner/repository',
  observations: '/observations',
  observationSetup: '/observations-setup',
  lessonPlanner: '/teacher/lesson-planner',
  teacherKekaAttendance: '/teacher/attendance',
  punctualityMails: '/teacher/punctuality-mails',
  // Per-teacher pivot of the Preschool Daily Updates audit. Day + Trend tabs,
  // the teacher dropdown and each teacher's class are all live from the backend
  // (classes = the batches a teacher is mapped to). See oh-api PRESCHOOL_DAILY_AUDIT_SPEC.md
  // (oh_moments) for the full data/logic.
  teacherUpdateCompliance: '/teacher/update-compliance',
  plannerTimetables: '/programs/planner/timetables',

  // Operations (was /class/inventory, /inventory/*, /centre, /scheduler/*)
  inventory: '/operations/inventory',
  orders: '/operations/orders',
  installmentOrders: '/operations/orders/instalment',
  dropInCareOrders: '/operations/orders/drop-in-care',
  centre: '/operations/centres',
  checklists: '/operations/checklists',
  parentOnboarding: '/operations/parent-onboarding',
  newsletter: '/operations/newsletter',
  fieldTrips: '/operations/field-trips',
  annualCalendar: '/operations/annual-calendar',
  teamWall: '/operations/team-wall',
  eventCreative: '/operations/event-creative',
  incidentReports: '/operations/incident-reports',
  underageConsent: '/operations/underage-consent',
  lifeCatchUps: '/operations/life-catch-ups',
  // Revenue Goals — centre-facing tab (My Centre) + HQ board/targets
  // (moved under HQ 2026-07-15 once the user got HQ access).
  revenueGoalsMyCentre: '/revenue-goals/my-centre',
  revenueGoalsAfterschoolFunnel: '/revenue-goals/afterschool-funnel',
  hqRevenueGoalsAllCentres: '/hq/revenue-goals/all-centres',
  hqRevenueGoalsSetTargets: '/hq/revenue-goals/set-targets',
  hqRevenueGoalsFollowUp: '/hq/revenue-goals/follow-up',
  // Interaction Audit — non-member prospect interaction coverage. Lives under
  // Centre Audit > Interaction Audit (moved out of HQ 2026-07-24); the
  // centre-facing My Centre tab was removed 2026-07-23.
  interactionAuditHqRollup: '/interaction-audit/hq-rollup',
  // HQ super-admin controls + agent audit for the procurement (Program Material)
  // reminder cycle. New leaf directly under the HQ group (2026-07-25).
  hqReminders: '/hq/reminders',
  hqBruceAccess: '/hq/bruce-access',
  // Parent CSAT survey responses (the MoEngage in-app survey posts them to
  // /oh_feedback/csat_response/). Sidebar entry is Super-Admin-only via
  // superAdminOnly — presentation only; the endpoint is open to any Alfred
  // login and the URL stays routable.
  hqCsat2026: '/hq/csat-2026',
  // Issue Tracker's HQ altitude — the read-only rollup over the board.
  // Moved out of the top-level tracker group 2026-08-18 (user: the board
  // and its owner routing are the tracker; the rollup is an HQ view).
  hqIssueTrackerDashboard: '/hq/issue-tracker/dashboard',
  hqIssueTrackerOwners: '/hq/issue-tracker/owners',
  hqIssueTrackerReport: '/hq/issue-tracker/report',
  // External Scheduler app, embedded as an iframe inside the MainLayout shell
  // (REACT_APP_SCHEDULER_URL). Path kept as the original /scheduler.
  scheduler: '/scheduler',

  // Support (was /communication/*, /community/classroom-moments)
  teacherParentChat: '/support/chat/teacher-parent',
  centreParentChat: '/support/chat/centre-parent',
  ticket: '/support/tickets',
  bulkMessaging: '/support/bulk-messaging',
  announcements: '/support/announcements',
  moments: '/support/moments',
  interactionHistory: '/support/interaction-history',
  openbook: '/support/openbook',
  analytics: '/support/analytics',
  analyticsCentreHealthV2: '/support/analytics/centre_health_v2',
  analyticsClusterGm: '/support/analytics/cluster_gm',
  analyticsSummerCamp: '/support/analytics/summer_camp',
  analyticsTeacher: '/support/analytics/teacher',
  analyticsBatchInventory: '/support/analytics/batch_inventory',
  // Teacher-facing analytics section (separate top-level group so it can be
  // granted to the teacher role without granting HQ). Reuses the
  // centre_health_v2 dashboard group — the last URL segment is the group key.
  centreAnalyticsCentreHealth: '/centre-analytics/centre_health_v2',
  centreAnalyticsAutoBooking: '/centre-analytics/auto-booking',

  // Procurement (centre-facing spend flows + HQ approvals)
  procurementDashboard: '/procurement/dashboard',
  procurementBudgets: '/procurement/budgets',
  procurementPettyCash: '/procurement/petty-cash',
  procurementConsumables: '/procurement/consumables',
  procurementProgramMaterial: '/procurement/program-material',
  procurementApprovals: '/procurement/approvals',
  procurementNotifications: '/procurement/notifications',
  // Two views (2026-06-06): Center Team (centre-facing, tabbed) + HQ (admin, tabbed)
  procurementCenterTeam: '/procurement/center-team',
  procurementHq: '/procurement/hq',
  cashRegister: '/money/cash-register',
  cashRegisterHq: '/money/cash-register/hq',
  // Centre Operations HQ — all-centre views (2026-07-11)
  hqCentreOpsOnboarding: '/hq/centre-ops/parent-onboarding',
  hqCentreOpsNewsletter: '/hq/centre-ops/newsletter',
  hqCentreOpsChecklist: '/hq/centre-ops/maintenance-checklist',
  hqCentreOpsCalendar: '/hq/centre-ops/annual-calendar',

  // HQ -> New Joiners: onboarding tracker for new staff (2026-10-08).
  hqNewJoiners: '/hq/new-joiners',

  // Roles & permissions (HQ). UI-only for now — nothing is enforced yet.
  rolesPermissions: '/hq/roles-permissions',

  // Apartments (Openhouse @ Apartment — isolated module; see
  // APARTMENT_CRM_EXECUTION_PLAN.md). Own section, not shown with preschool data.
  issues: '/issues',
  issueTracker: '/issue-tracker',
  apartmentOverview: '/apartments/overview',
  apartmentPerApartment: '/apartments/per-apartment',
  apartmentDashboard: '/apartments/dashboard',
  apartmentPeople: '/apartments/people',
  apartmentEnrolments: '/apartments/enrolments',
  apartmentPayments: '/apartments/payments',
  apartmentAttendance: '/apartments/attendance',
  apartmentTrials: '/apartments/trials',
  apartmentInventory: '/apartments/inventory',
  apartmentIssues: '/apartments/issues',
  apartmentPayout: '/apartments/payout',
  apartmentPricing: '/apartments/pricing',
  apartmentClusters: '/apartments/clusters',
  apartmentComplexes: '/apartments/complexes',
  apartmentProcurement: '/apartments/procurement',
  apartmentPurchaseRequests: '/apartments/purchase-requests',
  apartmentWaitlist: '/apartments/waitlist',
  apartmentClusterDashboard: '/apartments/cluster-dashboard',
  apartmentReports: '/apartments/reports',
  apartmentBatches: '/apartments/batches',
  // At-Home (1:1 classes at the child's home) — unrelated to At Apartment.
  atHome: '/at-home',
  atHomeSchedules: '/at-home/schedules',
  atHomeBookings: '/at-home/bookings',
  atHomeToBook: '/at-home/book',
  atHomeAttended: '/at-home/attended',
  atHomeDashboard: '/at-home/dashboard',
  atHomeIssues: '/at-home/issues',
  atHomeSetup: '/at-home/setup',
  teacherAtHome: '/teacher/at-home',
  apartmentStudents: '/apartments/students',

  // ACADS (Kolkata tuition business — fee mirror + reconciliation)
  acads: '/acads',
  acadsReview: '/acads/review'
}

export const additionalNavigationPaths = {
  demoScheduling: '/support/demo-scheduling',
  visitorEntry: '/operations/visitor-entry'
}

export const navigationPaths = {
  ...publicNavigationPaths,
  ...mainLayoutNavigationPaths,
  ...additionalNavigationPaths
}

// ---------------------------------------------------------------------------
// Sidebar IA (rewritten 2026-05-13, "Option A — Workflow-first")
// 7 top-level groups (was 13), grouped by what an admin does daily.
// All routes preserved (no URL changes in this phase).
// ---------------------------------------------------------------------------
// Sidebar restructure (2026-05-15): the six most-used pages are promoted
// to top-level sidebar entries; everything else is consolidated under a
// single collapsible "HQ" group. URLs / routes are unchanged so existing
// bookmarks keep working — only the sidebar visual structure shifts.
// ACADS on/off switch. On everywhere, production included, from the
// November 2026 go-live; routes derive from the nav, so false hides /acads.
// The backend has the matching switch (ACADS_ENABLED in prod.py) — flip both.
const ACADS_ENABLED = true

export const navigationMenus = [
  {
    name: 'home',
    displayName: 'Search',
    icon: { name: 'Parentdashboard' },
    subMenus: [
      // Auto-collapse: single visible leaf → group renders as a direct link.
      {
        name: 'search',
        displayName: 'Search',
        path: navigationPaths.parentdashboard,
        component: 'DashboardPage'
      },
      {
        name: 'teacherdashboard',
        path: navigationPaths.teacherdashboard,
        component: 'DashboardPage',
        sideBarVisibility: false
      }
    ]
  },
  {
    // Promoted out of HQ -> People (2026-07).
    name: 'usersGroup',
    displayName: 'Users',
    icon: { name: 'People' },
    subMenus: [
      {
        name: 'users',
        displayName: 'Users',
        path: navigationPaths.student,
        component: 'UsersPage'
      },
      {
        name: 'usersParent',
        path: navigationPaths.parent,
        component: 'UsersPage',
        sideBarVisibility: false
      },
      {
        name: 'usersTeacher',
        path: navigationPaths.teacher,
        component: 'UsersPage',
        sideBarVisibility: false
      },
      {
        name: 'usersAdmin',
        path: navigationPaths.admin,
        component: 'UsersPage',
        sideBarVisibility: false
      }
    ]
  },
  {
    name: 'attendance',
    displayName: 'Attendance',
    icon: { name: 'Attendance' },
    subMenus: [
      {
        name: 'markattendance',
        displayName: 'Mark Attendance',
        path: navigationPaths.attendance,
        component: 'AttendancePage'
      }
    ]
  },
  {
    name: 'booking',
    displayName: 'Booking',
    icon: { name: 'Inventory' },
    subMenus: [
      {
        name: 'inventory',
        displayName: 'Inventory',
        path: navigationPaths.inventory,
        component: 'ClassInventoryPage'
      }
    ]
  },
  {
    name: 'walkIn',
    displayName: 'Walk-in',
    icon: { name: 'VisitorEntry' },
    subMenus: [
      {
        name: 'visitorEntry',
        displayName: 'Visitor Entry',
        path: navigationPaths.visitorEntry,
        component: 'VisitorEntryPage'
      }
    ]
  },
  {
    name: 'bookMakeup',
    displayName: 'Book Make-up',
    icon: { name: 'Event' },
    subMenus: [
      {
        name: 'bookMakeupSession',
        displayName: 'Book Make-Up Session',
        path: navigationPaths.bookMakeupSession,
        component: 'ClassMakeUpSessionBookingPage'
      }
    ]
  },
  {
    // Promoted out of HQ -> Classes (2026-07).
    name: 'enrolmentsGroup',
    displayName: 'Enrolments',
    icon: { name: 'Enrolment' },
    subMenus: [
      {
        name: 'enrolments',
        displayName: 'Enrolments',
        path: navigationPaths.enrolment,
        component: 'EnrolmentsPage'
      },
      {
        name: 'enrolmentsExcel',
        path: navigationPaths.excelEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      },
      {
        name: 'enrolmentsExplore',
        path: navigationPaths.exploreEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      },
      {
        name: 'enrolmentsPSAC',
        path: navigationPaths.psacEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      },
      {
        name: 'enrolmentsActiveCare',
        path: navigationPaths.activeCareEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      },
      {
        name: 'enrolmentsToddlerClub',
        path: navigationPaths.toddlerClubEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      },
      {
        name: 'enrolmentsTrial',
        path: navigationPaths.trialEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      },
      {
        name: 'enrolmentsMakeUp',
        path: navigationPaths.makeupEnrolment,
        component: 'EnrolmentsPage',
        sideBarVisibility: false
      }
    ]
  },
  {
    name: 'moments',
    displayName: 'Moments',
    icon: { name: 'Portfolio' },
    subMenus: [
      {
        name: 'classroom-moments',
        displayName: 'Moments',
        path: navigationPaths.moments,
        component: 'Moments'
      }
    ]
  },
  {
    // Promoted out of HQ -> Operations (2026-07).
    name: 'ordersGroup',
    displayName: 'Orders',
    icon: { name: 'Payment' },
    subMenus: [
      {
        name: 'orders',
        displayName: 'Orders',
        path: navigationPaths.orders,
        component: 'OrdersPage'
      },
      {
        name: 'ordersInstalment',
        path: navigationPaths.installmentOrders,
        component: 'OrdersPage',
        sideBarVisibility: false
      },
      {
        name: 'ordersDropInCare',
        path: navigationPaths.dropInCareOrders,
        component: 'OrdersPage',
        sideBarVisibility: false
      }
    ]
  },
  {
    name: 'chat',
    displayName: 'Chat',
    icon: { name: 'Chat' },
    // Flattened: top-level Chat expands directly to Teacher–Parent /
    // Centre–Parent (was Support → Chat → these two; one extra click).
    subMenus: [
      {
        name: 'teacher-parent chat',
        displayName: 'Teacher–Parent',
        path: navigationPaths.teacherParentChat,
        component: 'TeacherParentChat'
      },
      {
        name: 'centre-parent chat',
        displayName: 'Centre–Parent',
        path: navigationPaths.centreParentChat,
        component: 'CentreParentChat'
      }
    ]
  },
  {
    name: 'teacher',
    displayName: 'Teacher',
    icon: { name: 'People' },
    // Keep it an expandable dropdown even with one child — more teacher
    // features (beyond Lesson Planner) will live here.
    noAutoCollapse: true,
    subMenus: [
      {
        name: 'lessonPlanner',
        displayName: 'Lesson Planner',
        path: navigationPaths.lessonPlanner,
        component: 'LessonPlannerPage'
      },
      {
        name: 'preschoolWeeklyEvaluation',
        displayName: 'Weekly Evaluation',
        path: navigationPaths.preschoolWeeklyEvaluation,
        component: 'PreschoolWeeklyEvaluationPage'
      },
      {
        name: 'preschoolWeeklyEvaluationView',
        displayName: 'Weekly Submissions (View)',
        path: navigationPaths.preschoolWeeklyEvaluationView,
        component: 'PreschoolWeeklyEvaluationViewPage'
      },
      {
        name: 'preschoolTermReport',
        displayName: 'Term Report',
        path: navigationPaths.preschoolTermReport,
        component: 'PreschoolTermReportPage'
      },
      {
        // At-Home (2026-09-29): a teacher's own 1:1 home sessions — schedule,
        // arrival/attendance/notes, issues. Backend scopes to the logged-in teacher.
        name: 'teacherAtHome',
        displayName: 'At Home',
        path: navigationPaths.teacherAtHome,
        component: 'AtHomeTeacherPage'
      },
      {
        // Static preview, backend not wired yet - see Audits > Preschool
        // Daily Updates for the query logic this pivots per-teacher.
        name: 'teacherUpdateCompliance',
        displayName: 'Daily Updates Report',
        path: navigationPaths.teacherUpdateCompliance,
        component: 'TeacherUpdateCompliancePage'
      }
    ]
  },
  {
    // Teacher-facing analytics. A separate top-level section (not under HQ) so
    // the teacher role can be granted THIS in Roles & Permissions without
    // getting HQ. Reuses the same dashboard definitions as HQ -> Analytics ->
    // Centre Health (the /centre_health_v2 group) — same tables, in sync.
    name: 'centreAnalytics',
    displayName: 'Centre Analytics',
    icon: { name: 'Reports' },
    // Keep it an expandable dropdown even with one child — more analytics
    // sections can live here later.
    noAutoCollapse: true,
    subMenus: [
      {
        name: 'centreAnalyticsCentreHealth',
        displayName: 'Centre Health',
        path: navigationPaths.centreAnalyticsCentreHealth,
        component: 'AnalyticsPage'
      },
      {
        // The auto-booking review board. Centre teams read next month's dry
        // run and comment per parent; HQ answers in a second column. NOT an
        // AnalyticsPage — the comments are writes, and the analytics
        // framework is read-only SQL (oh_analytics/sql_runner FORBIDDEN_SQL).
        name: 'centreAnalyticsAutoBooking',
        displayName: 'Auto-Booking',
        path: navigationPaths.centreAnalyticsAutoBooking,
        component: 'AutoBookingPage'
      }
    ]
  },
  {
    // Cash Register — top-level, just above Centre Operations (user,
    // 2026-08-27; moved OUT of Procurement & Expenses). One visible child, so
    // the sidebar folds this group into a single link straight to the ledger
    // (the issueTracker pattern). The HQ view lives under HQ (leaf
    // `cashRegisterHq`, just above HQ › Centre Operations — user, same day).
    // The permission id is the dotted chain of `name`s —
    // `cashRegister.cashRegisterLedger` is granted by user migration 0066;
    // renaming either `name` orphans that grant.
    name: 'cashRegister',
    displayName: 'Cash Register',
    icon: { name: 'CashRegister' },
    subMenus: [
      {
        name: 'cashRegisterLedger',
        displayName: 'Cash Register',
        path: navigationPaths.cashRegister,
        component: 'CashRegisterPage'
      }
    ]
  },
  {
    name: 'centreOperations',
    displayName: 'Centre Operations',
    icon: { name: 'CentreOperations' },
    subMenus: [
      {
        name: 'lifeCatchUps',
        displayName: 'Life Catch-Ups',
        path: navigationPaths.lifeCatchUps,
        component: 'LifeCatchUpsPage'
      },
      {
        name: 'checklists',
        displayName: 'Maintenance Checklist',
        path: navigationPaths.checklists,
        component: 'ChecklistsPage'
      },
      {
        name: 'parentOnboarding',
        displayName: 'Parent Onboarding',
        path: navigationPaths.parentOnboarding,
        component: 'ParentOnboardingPage'
      },
      {
        // Flattened out of the former "Creatives" group (2026-07-10 feedback):
        // these sit alongside Parent Onboarding / Life Catch-Ups directly.
        name: 'newsletter',
        displayName: 'Newsletter',
        path: navigationPaths.newsletter,
        component: 'NewsletterPage'
      },
      {
        // Field Trips (user, 2026-10-12) — was its own sidebar entry above
        // Chat; moved in here 2026-10-17 (user). Permission id is now
        // `centreOperations.fieldTrips`, granted by user migration 0073.
        name: 'fieldTrips',
        displayName: 'Field Trips',
        path: navigationPaths.fieldTrips,
        component: 'FieldTripsPage'
      },
      {
        // Annual Calendar (user, 2026-10-26) — read-only here; HQ edits it
        // under HQ › Centre Operations › Annual Calendar. Permission id
        // `centreOperations.annualCalendar`, granted by user migration 0075.
        name: 'annualCalendar',
        displayName: 'Annual Calendar',
        path: navigationPaths.annualCalendar,
        component: 'AnnualCalendarPage'
      },
      {
        name: 'teamWall',
        displayName: 'Centre Team Roster',
        path: navigationPaths.teamWall,
        component: 'TeamWallPage'
      },
      {
        name: 'eventCreative',
        displayName: 'Event Creative',
        path: navigationPaths.eventCreative,
        component: 'EventCreativePage'
      },
      {
        name: 'incidentReports',
        displayName: 'Incident Reports',
        path: navigationPaths.incidentReports,
        component: 'IncidentReportsPage'
      },
      {
        name: 'underageConsent',
        displayName: 'Underage Consent',
        path: navigationPaths.underageConsent,
        component: 'UnderageConsentPage'
      },
      {
        // Moved out of the former "Center Team" group (2026-06-06).
        name: 'centerTeamProcurement',
        displayName: 'Procurement & Expenses',
        path: navigationPaths.procurementCenterTeam,
        component: 'ProcurementCenterTeamPage'
      },
      {
        // Moved from HQ + renamed (was "Observations").
        name: 'observations',
        displayName: 'Classroom Observations',
        path: navigationPaths.observations,
        component: 'PlannerPlansPage'
      }
    ]
  },
  {
    // Centre Audit — was HQ > Audits, moved OUT of HQ to its own top-level
    // group directly below Centre Operations (2026-07-24, user). Both pages
    // read per-centre, so they belong next to the centre pages rather than
    // buried in HQ. Paths are unchanged, so existing links still resolve; the
    // permission ids DID change (hq.hqAudits.* -> centreAudit.*) — see user
    // migration 0056_move_audits_out_of_hq.
    name: 'centreAudit',
    displayName: 'Centre Audit',
    icon: { name: 'CentreAudit' },
    subMenus: [
      {
        // Static preview, backend not wired yet - real numbers from a one-off read-only
        // audit against oh_admin_prod (17 Jul 2026). See scripts/audit_parent_updates.py
        // and docs/audits/ in the centre-toolkit repo for the query logic and full report.
        name: 'preschoolDailyUpdatesAudit',
        displayName: 'Preschool Daily Updates',
        path: navigationPaths.preschoolDailyUpdatesAudit,
        component: 'PreschoolDailyUpdatesPage'
      },
      {
        // Interactions Audit — non-member prospect follow-up coverage. ONE page
        // for everyone again (user, 2026-09-10): the daily board (Overview +
        // Visit type + Trends, drill anywhere), which the API narrows to the
        // user's own centres. The 2026-09-08 split to a centre chase list is
        // reverted; pages/InteractionAudit/index.js + MyCentre.js are kept on
        // disk if it is ever wanted back. The "Declined subscription" tick-box
        // is unaffected — HqRollup.js passes allowDeclinedMark too, so the
        // centre team still ticks it while logging.
        name: 'prospectInteractionAudit',
        // displayName ONLY (2026-08-06) — `name` and therefore the permission id
        // centreAudit.prospectInteractionAudit are unchanged, so no role
        // migration is needed and nobody loses access. Matches the name Gordon
        // now puts at the top of the Slack thread.
        displayName: 'Interactions Audit',
        path: navigationPaths.interactionAuditHqRollup,
        component: 'InteractionAuditHqRollupPage'
      },
      {
        // Keka clock-in register (2026-09-30): every staff member's week from
        // Keka's Daily Performance report. Was Teacher > Attendance; moved here
        // 2026-10-17 (user). Permission id centreAudit.kekaAttendance (user
        // migration 0074 moved the grant) is granted to NO role by default —
        // it shows everyone's lateness, so HQ ticks it per role; the API
        // enforces the same id.
        name: 'kekaAttendance',
        displayName: 'Teacher Attendance',
        path: navigationPaths.teacherKekaAttendance,
        component: 'TeacherAttendancePage'
      },
      {
        // Review and send a day's punctuality mails (2026-11-02). Permission
        // id centreAudit.punctualityMails is granted to NO role by default: it
        // mails staff and changes pay, so HQ ticks it per role; the API
        // enforces the same id.
        name: 'punctualityMails',
        displayName: 'Punctuality Mails',
        path: navigationPaths.punctualityMails,
        component: 'PunctualityMailsPage'
      },
      {
        // Teacher Scorecard (2026-10-17): punctuality (Keka), moments,
        // environment setup and facilitation (CD/DCD weekly form), each out
        // of 5, per teacher + trend. Permission id centreAudit.teacherScorecard
        // is granted to NO role by default — like Attendance it shows
        // everyone's lateness — so HQ ticks it per role; the API enforces the
        // same id.
        // 28 Oct (Satya): moved to Centre Analytics › Centre Health ›
        // Preschool Quality & Ops. Off the sidebar; the old link forwards
        // there. keepPermission keeps the tick box in Roles & Permissions —
        // the id is unchanged, so roles and the API's check need no change.
        name: 'teacherScorecard',
        displayName: 'Teacher Scorecard',
        path: navigationPaths.teacherScorecard,
        component: 'TeacherScorecardMovedPage',
        sideBarVisibility: false,
        keepPermission: true
      },
      {
        // HQ's visit audits, were the Centre Audits sheet (Satya, 30 Oct). They
        // carry remarks about named staff, so like Teacher Attendance both ids
        // are granted to NO role by default: HQ ticks them per role. The API
        // enforces the same ids (oh_operations/api/alfred_centre_audits.py).
        name: 'centreVisitAudit',
        displayName: 'Centre Audit',
        path: navigationPaths.centreVisitAudit,
        component: 'CentreVisitAuditPage'
      },
      {
        name: 'hkAudit',
        displayName: 'Housekeeping Audit',
        path: navigationPaths.hkAudit,
        component: 'HkAuditPage'
      }
    ]
  },
  {
    // Revenue Goals — monthly revenue target vs already-committed per centre
    // (preschool installments paid in advance + manual override; wallet
    // component and the parent chase list land in P2).
    name: 'revenueGoals',
    displayName: 'Grow & Retain',
    icon: { name: 'RevenueGoals' },
    subMenus: [
      {
        // Centre teams' daily view: my gap + who to call. Centre-mapped
        // users only ever get their own centre back from the API. The
        // all-centre board + Set Targets live under HQ -> Revenue Goals.
        // The centre team's worklist — the only page in this section. The
        // name/path/component keys keep their old `myCentre` ids on purpose:
        // that string is the permission id, and renaming it would hide the
        // page until a migration granted the new one (see reference: a new tab
        // id = nobody can see it). Only the label changes.
        name: 'myCentre',
        displayName: 'Grow & Retain',
        path: navigationPaths.revenueGoalsMyCentre,
        component: 'RevenueGoalsMyCentrePage'
      }
    ]
  },
  {
    // Issue Tracker — the board behind the centre-facing issue form.
    // Not the same thing as "Issues & Notes" (oh_apartments), whose sidebar
    // entry was removed (2026-10); its page code is still on disk.
    // The permission id is the dotted chain of `name`s, so `issueTrackerBoard`
    // MUST stay `issueTracker > issueTrackerBoard` (user migration 0060) in
    // lockstep with the grant — move a leaf and its grant points at nothing.
    // Both the other leaves have since done exactly that, each with a migration
    // renaming its id: the rollup left for HQ (0063) and Issue Owners followed
    // it (0064). One visible child is left, so the sidebar folds this group
    // into a single "Issue Tracker" link straight to the board — which is the
    // intent (user, 2026-08-18): one tab for the people raising and working
    // issues, everything HQ-facing under HQ.
    name: 'issueTracker',
    displayName: 'Issue Tracker',
    icon: { name: 'Reports' },
    subMenus: [
      {
        name: 'issueTrackerBoard',
        displayName: 'All issues',
        path: navigationPaths.issueTracker,
        component: 'IssueTrackerPage'
      }
    ]
  },
  {
    // Hidden (user, 2026-09-30): the Issue Tracker is live and the Google Form
    // it opened is closing, so issues are raised from Issue Tracker only. Kept,
    // not deleted, so it can come back by removing sideBarVisibility.
    name: 'getHelp',
    displayName: 'Get Help',
    icon: { name: 'Help' },
    externalLink: true,
    sideBarVisibility: false
  },
  {
    // HQ — everything not promoted to a top-level tab.
    // 2026-07: Users, Enrolments and Orders promoted OUT to their own top-level
    // groups; Batches + Summer Camp Batches merged into one tabbed page. The
    // People / Classes / Operations sub-groups each had exactly one visible item
    // left afterwards, so each was replaced by that item. The Support umbrella
    // was dissolved — its items now sit directly under HQ.
    name: 'hq',
    displayName: 'HQ',
    icon: { name: 'Reports' },
    subMenus: [
      {
        name: 'scheduler',
        displayName: 'Scheduler',
        path: navigationPaths.scheduler,
        component: 'SchedulerPage'
      },
      {
        // Super-admin / HQ-admin controls + agent audit for the procurement
        // (Program Material) reminder cycle. Direct leaf under HQ, so its
        // permission id is `hq.reminders` — kept in lockstep with user
        // migration 0057_grant_reminder_controls. Visible label is the agent's
        // name ("Gordon"); `name`/perm id stays `reminders` so the grant holds.
        name: 'reminders',
        displayName: 'Gordon',
        path: navigationPaths.hqReminders,
        component: 'RemindersPage'
      },
      {
        // Bruce's owner switches people on and off here (Satya, 2026-09-19).
        // bruceOwnerOnly hides the entry from everyone but the owner (the
        // /assistant/config `can_manage_access` flag, via MainLayout). That is
        // presentation only — the real lock is server-side: the endpoints
        // behind the page refuse anyone who is not on BRUCE_OWNER_PHONES.
        name: 'bruceAccess',
        displayName: 'Bruce',
        path: navigationPaths.hqBruceAccess,
        component: 'BruceAccessPage',
        bruceOwnerOnly: true
      },
      {
        // Parent CSAT responses from the MoEngage in-app survey.
        //
        // superAdminOnly hides the sidebar entry from everyone else. Widened
        // from a two-number allow-list to all Super Admins (user, 2026-09-09).
        // Note this gates on the Super Admin ROLE, not `access.unrestricted` —
        // that flag is also true for a user with no role, and on the fail-open
        // when /base/me errors, so it would show the tab to the least
        // privileged accounts.
        //
        // Still PRESENTATION ONLY and deliberately so: the
        // /oh_feedback/csat_response/alfred/ endpoints behind it are open to any
        // authenticated Alfred user, and the route stays reachable by URL. The
        // page repeats the check so a typed URL shows a notice rather than the
        // data, but neither is a data-access control. If this needs to become
        // one, give it a permission id and a grant migration like the leaves
        // around it.
        name: 'hqCsat2026',
        displayName: 'CSAT 2026',
        path: navigationPaths.hqCsat2026,
        component: 'CsatPage',
        superAdminOnly: true
      },
      {
        // Issue Tracker at HQ altitude. Nested so it can carry more tabs as
        // they land — Dashboard is the first, not the only one. The tracker's
        // own group (board + Issue Owners) stays top-level and wide-access;
        // what lives here is the management read over it.
        // Permission ids are the dotted chain, so this leaf is
        // `hq.hqIssueTracker.hqIssueTrackerDashboard` — in lockstep with user
        // migration 0063, which also retires the old `issueTracker.issuePulse`.
        name: 'hqIssueTracker',
        displayName: 'Issue Tracker',
        nested: true,
        // A no-op while there are two leaves, kept because the sidebar folds a
        // single-leaf group into a direct link — which would swallow whichever
        // label was left if one of these ever moves out again.
        noAutoCollapse: true,
        subMenus: [
          {
            name: 'hqIssueTrackerDashboard',
            displayName: 'Dashboard',
            path: navigationPaths.hqIssueTrackerDashboard,
            component: 'IssuePulsePage'
          },
          {
            // Every open issue, centre by category, split at seven days —
            // Satya's format (2026-09-09), the one the old tracker's sheet
            // produced by hand. Sits beside the Dashboard because they answer
            // different questions over the same data: the Dashboard is "are we
            // okay this week", this is "where exactly is it backed up".
            // Gordon posts the same numbers as prose every Monday 09:00 IST
            // (apps/oh_issues/tasks.py); this page is what that post links to.
            // Permission id `hq.hqIssueTracker.hqIssueTrackerReport`, in
            // lockstep with user migration 0068.
            name: 'hqIssueTrackerReport',
            displayName: 'Pending Report',
            path: navigationPaths.hqIssueTrackerReport,
            component: 'IssuePendingReportPage'
          },
          {
            // Who each category routes to, on which Slack id, and which Alfred
            // account may close its issues. Moved here 2026-08-18 (user): owner
            // routing is company-wide config that only HQ should touch — which
            // is what its Gordon-only grant already said — so HQ is where it
            // belongs. Third home, and the last: /hq/issue-owners originally,
            // then the tracker group on 08-16, now here.
            // Permission id `hq.hqIssueTracker.hqIssueTrackerOwners`, in
            // lockstep with user migration 0064.
            name: 'hqIssueTrackerOwners',
            displayName: 'Issue Owners',
            path: navigationPaths.hqIssueTrackerOwners,
            component: 'IssueOwnersPage'
          }
        ]
      },
      {
        // Onboarding tracker for new staff — one row per joiner, one tick per
        // step (offer letter through asset mapping). Tracker only: nothing is
        // sent from here (user, 2026-10-08). Permission id `hq.newJoiners`;
        // the oh_operations new_joiners endpoints check the same id, so
        // granting it on Roles & Permissions is what opens the data too.
        // Super Admins see it with no grant.
        name: 'newJoiners',
        displayName: 'New Joiners',
        path: navigationPaths.hqNewJoiners,
        component: 'NewJoinersPage'
      },
      {
        // Cash Register, the HQ half — cash in hand across centres + the
        // deposit/challan ledger. Directly under HQ, just above Centre
        // Operations (user, 2026-08-27). Permission id `hq.cashRegisterHq`,
        // in lockstep with user migration 0066.
        name: 'cashRegisterHq',
        displayName: 'Cash Register',
        path: navigationPaths.cashRegisterHq,
        component: 'CashRegisterHqPage'
      },
      // ACADS HQ view — finance's page: the month dashboard, the collection
      // teacher-wise and day-wise, and the review that closes the month. Lives
      // in HQ, not under ACADS, so the centre team that fills Fees never sees
      // it (user, 2026-10-09; dashboard moved here 2026-10-12). Permission id
      // stays `hq.hqAcadsReview`, in lockstep with user migration 0071.
      ...(ACADS_ENABLED
        ? [
            {
              name: 'hqAcadsReview',
              displayName: 'ACADS HQ view',
              path: navigationPaths.acadsReview,
              component: 'AcadsReviewPage'
            }
          ]
        : []),
      {
        // All-centre views of the Centre Operations pages + HQ procurement
        // (moved in here 2026-07-11).
        name: 'hqCentreOps',
        displayName: 'Centre Operations',
        nested: true,
        subMenus: [
          {
            name: 'hqCentreOpsOnboarding',
            displayName: 'Parent Onboarding',
            path: navigationPaths.hqCentreOpsOnboarding,
            component: 'CentreOpsHqPage'
          },
          {
            name: 'hqCentreOpsNewsletter',
            displayName: 'Newsletter',
            path: navigationPaths.hqCentreOpsNewsletter,
            component: 'CentreOpsHqPage'
          },
          {
            // HQ checkpoint editor for the centre maintenance checklists.
            // Permission id hq.hqCentreOps.hqCentreOpsChecklist — granted by
            // base/user migration 0058 (same-PR rule for new leaves).
            name: 'hqCentreOpsChecklist',
            displayName: 'Maintenance Checklist',
            path: navigationPaths.hqCentreOpsChecklist,
            component: 'ChecklistSetupPage'
          },
          {
            // The Annual Calendar editor (user, 2026-10-26). Permission id
            // hq.hqCentreOps.hqCentreOpsCalendar — granted by user migration
            // 0075 and checked again by the API before any change.
            name: 'hqCentreOpsCalendar',
            displayName: 'Annual Calendar',
            path: navigationPaths.hqCentreOpsCalendar,
            component: 'AnnualCalendarHqPage'
          },
          {
            // HQ/admin procurement — tabbed (Budgets / Approvals). 2026-06-06.
            name: 'hqProcurement',
            displayName: 'Procurement & Expenses',
            path: navigationPaths.procurementHq,
            component: 'ProcurementHqPage'
          }
        ]
      },
      {
        // All-centre Revenue Goals: the board + target entry. Same name as
        // the centre-facing tab on purpose — one feature, two altitudes.
        // HIDDEN from the sidebar since 2026-09-22 (Satya: "remove revenue
        // goals tab (hide)"): every leaf carries `sideBarVisibility: false`,
        // which keeps the route and the permission id and drops the tab — a
        // group with no visible child is not drawn. Un-hiding is deleting the
        // three flags.
        name: 'hqRevenueGoals',
        displayName: 'Revenue Goals',
        nested: true,
        subMenus: [
          {
            name: 'hqRevenueGoalsAllCentres',
            displayName: 'All Centres',
            path: navigationPaths.hqRevenueGoalsAllCentres,
            component: 'RevenueGoalsBoardPage',
            sideBarVisibility: false
          },
          {
            name: 'hqRevenueGoalsSetTargets',
            displayName: 'Set Targets',
            path: navigationPaths.hqRevenueGoalsSetTargets,
            component: 'RevenueGoalsSetTargetsPage',
            sideBarVisibility: false
          },
          {
            name: 'hqRevenueGoalsFollowUp',
            displayName: 'Follow-up',
            path: navigationPaths.hqRevenueGoalsFollowUp,
            component: 'RevenueGoalsHqRollupPage',
            sideBarVisibility: false
          }
        ]
      },
      {
        // was: HQ -> People -> Guardian Requests
        name: 'mergeRequests',
        displayName: 'Guardian Requests',
        path: navigationPaths.mergeRequests,
        component: 'MergeRequestsPage'
      },
      {
        // was: HQ -> Classes -> Batches (Summer Camp is now a tab on this page)
        name: 'batch',
        displayName: 'Batches',
        path: navigationPaths.batch,
        component: 'ClassBatchesPage'
      },
      {
        // Old summer-camp URL still resolves; the page opens on that tab.
        name: 'summerCampBatch',
        path: navigationPaths.summerCampBatch,
        component: 'ClassBatchesPage',
        sideBarVisibility: false
      },
      {
        name: 'sessions',
        path: navigationPaths.trialsessions,
        component: 'SessionsPage',
        sideBarVisibility: false
      },
      {
        name: 'sessionsExplore',
        path: navigationPaths.exploreSession,
        component: 'SessionsPage',
        sideBarVisibility: false
      },
      {
        name: 'sessionsToddlerClub',
        path: navigationPaths.toddlerClubSession,
        component: 'SessionsPage',
        sideBarVisibility: false
      },
      {
        name: 'sessionsMakeUp',
        path: navigationPaths.makeUpSession,
        component: 'SessionsPage',
        sideBarVisibility: false
      },
      {
        name: 'hqPrograms',
        displayName: 'Programs',
        nested: true,
        subMenus: [
          {
            name: 'programs',
            displayName: 'All',
            path: navigationPaths.subjectCategory,
            component: 'ProgramsPage'
          },
          {
            name: 'programsSubjects',
            path: navigationPaths.subject,
            component: 'ProgramsPage',
            sideBarVisibility: false
          },
          {
            name: 'programsSubjectsPlaydate',
            path: navigationPaths.playdateSubject,
            component: 'ProgramsPage',
            sideBarVisibility: false
          },
          {
            name: 'programsSubjectsToddlerClub',
            path: navigationPaths.toddlerClubSubject,
            component: 'ProgramsPage',
            sideBarVisibility: false
          },
          {
            name: 'programsStandards',
            path: navigationPaths.standard,
            component: 'ProgramsPage',
            sideBarVisibility: false
          },
          {
            name: 'programsCourses',
            path: navigationPaths.course,
            component: 'ProgramsPage',
            sideBarVisibility: false
          },
          // Preschool curriculum (Phase 5) — see
          // new-code/docs/weekly-skills-migration/phase-5-admin-portal/
          {
            name: 'preschoolWeekBuilder',
            displayName: 'Preschool: Week Builder',
            path: navigationPaths.preschoolWeekBuilder,
            component: 'PreschoolWeekBuilderPage'
          },
          {
            name: 'preschoolSkills',
            displayName: 'Preschool: Skills',
            path: navigationPaths.preschoolSkill,
            component: 'PreschoolSkillsPage'
          },
          {
            name: 'preschoolMonthlyThemes',
            displayName: 'Preschool: Monthly Themes',
            path: navigationPaths.preschoolMonthlyTheme,
            component: 'PreschoolMonthlyThemesPage'
          },
          {
            name: 'preschoolAnnualLos',
            displayName: 'Preschool: Annual LOs',
            path: navigationPaths.preschoolAnnualLo,
            component: 'PreschoolAnnualLOsPage'
          },
          {
            name: 'preschoolSpiralLos',
            displayName: 'Preschool: Spiral LOs',
            path: navigationPaths.preschoolSpiralLo,
            component: 'PreschoolSpiralLOsPage'
          }
        ]
      },
      {
        name: 'hqPreschoolPlanner',
        displayName: 'Preschool Planner',
        nested: true,
        subMenus: [
          {
            name: 'plannerGradeGroups',
            displayName: 'Planner: Grade Groups',
            path: navigationPaths.plannerGradeGroups,
            component: 'PlannerGradeGroupsPage'
          },
          {
            name: 'plannerSubjects',
            displayName: 'Planner: Subjects',
            path: navigationPaths.plannerSubjects,
            component: 'PlannerSubjectsPage'
          },
          {
            name: 'plannerLayouts',
            displayName: 'Planner: Layouts',
            path: navigationPaths.plannerLayouts,
            component: 'PlannerLayoutsPage'
          },
          {
            name: 'plannerQuotas',
            displayName: 'Planner: Quotas',
            path: navigationPaths.plannerQuotas,
            component: 'PlannerQuotasPage'
          },
          {
            name: 'plannerInventory',
            displayName: 'Planner: Inventory',
            path: navigationPaths.plannerInventory,
            component: 'PlannerInventoryPage'
          },
          {
            name: 'plannerRepository',
            displayName: 'Planner: Lesson Plans',
            path: navigationPaths.plannerRepository,
            component: 'PlannerRepositoryPage'
          },
          {
            name: 'plannerTimetables',
            displayName: 'Planner: Timetables',
            path: navigationPaths.plannerTimetables,
            component: 'PlannerTimetablesPage'
          }
        ]
      },
      {
        name: 'observationSetup',
        displayName: 'Observation Setup',
        path: navigationPaths.observationSetup,
        component: 'ObservationSetupPage'
      },
      {
        // was: HQ -> Operations -> Centres
        name: 'centre',
        displayName: 'Centres',
        path: navigationPaths.centre,
        component: 'SpaceCentrePage'
      },
      {
        name: 'ticket',
        displayName: 'Tickets',
        path: navigationPaths.ticket,
        component: 'TicketPage'
      },
      {
        name: 'bulk-messaging',
        displayName: 'Bulk Messaging',
        path: navigationPaths.bulkMessaging,
        component: 'CommunicationBulkMessagingPage',
        sideBarVisibility: false
      },
      {
        name: 'announcements',
        displayName: 'App Announcements',
        path: navigationPaths.announcements,
        component: 'AnnouncementPage'
      },
      {
        name: 'interaction-history',
        displayName: 'Interaction History',
        path: navigationPaths.interactionHistory,
        component: 'InteractionHistoryPage'
      },
      {
        name: 'openbook',
        displayName: 'Openbook',
        path: navigationPaths.openbook,
        component: 'Openbook'
      },
      {
        name: 'analytics',
        displayName: 'Analytics',
        nested: true,
        subMenus: [
          {
            name: 'analyticsCentreHealthV2',
            displayName: 'Centre Health',
            path: navigationPaths.analyticsCentreHealthV2,
            component: 'AnalyticsPage'
          },
          {
            name: 'analyticsClusterGm',
            displayName: 'Cluster Manager & GM',
            path: navigationPaths.analyticsClusterGm,
            component: 'AnalyticsPage'
          },
          {
            name: 'analyticsSummerCamp',
            displayName: 'Summer Camp 2026',
            path: navigationPaths.analyticsSummerCamp,
            component: 'AnalyticsPage'
          },
          {
            name: 'analyticsTeacher',
            displayName: 'Teacher',
            path: navigationPaths.analyticsTeacher,
            component: 'AnalyticsPage'
          },
          {
            name: 'analyticsBatchInventory',
            displayName: 'Batch Inventory',
            path: navigationPaths.analyticsBatchInventory,
            component: 'AnalyticsPage'
          }
        ]
      },
      {
        // Roles & Permissions — tabbed (Roles / People). Added 2026-07.
        // UI-only: no backend, no enforcement. Permissions == the Alfred tab tree.
        name: 'rolesPermissions',
        displayName: 'Roles & Permissions',
        path: navigationPaths.rolesPermissions,
        component: 'RolesPermissionsPage'
      }
    ]
  },
  {
    // Apartments — Openhouse @ Apartment, an isolated module with its own
    // section. Frontend-first build (Dashboard + Payments live; rest scaffolded).
    name: 'apartments',
    displayName: 'At Apartment',
    icon: { name: 'Apartments' },
    subMenus: [
      {
        // Parent/section landing — org-wide rollup across all apartments, with
        // a cluster filter. Not tied to the apartment selector.
        name: 'apartmentOverview',
        displayName: 'Overview',
        path: navigationPaths.apartmentOverview,
        component: 'ApartmentOverviewPage'
      },
      {
        // Single-apartment workspace — one page whose sections render as top
        // tabs (cleaner than a 15-item fly-out). Scoped to the apartment picked
        // in the selector. Tab components are composed in PerApartment/index.js.
        name: 'apartmentManage',
        displayName: 'Per Apartment',
        path: navigationPaths.apartmentPerApartment,
        component: 'ApartmentPerApartmentPage'
      },
      {
        name: 'apartmentClusterDashboard',
        displayName: 'Cluster Dashboard',
        path: navigationPaths.apartmentClusterDashboard,
        component: 'ApartmentClusterDashboardPage'
      },
      {
        // At Apartment procurement (2026-07-17): LXD raises requests ->
        // cluster manager -> At Apartment head -> HQ fulfils (HQ queue lives
        // under HQ -> Procurement & Expenses -> At Apartment).
        name: 'apartmentProcurement',
        displayName: 'Procurement',
        path: navigationPaths.apartmentProcurement,
        component: 'ApartmentProcurementPage'
      },
      {
        name: 'apartmentReports',
        displayName: 'Reports & MIS',
        path: navigationPaths.apartmentReports,
        component: 'ApartmentReportsPage'
      },
      {
        name: 'apartmentClusters',
        displayName: 'Clusters',
        path: navigationPaths.apartmentClusters,
        component: 'ApartmentClustersPage'
      },
      {
        name: 'apartmentComplexes',
        displayName: 'Apartments',
        path: navigationPaths.apartmentComplexes,
        component: 'ApartmentComplexesPage'
      }
    ]
  },
  {
    // At-Home (2026-09-29): 1:1 classes taught at the child's home. A different
    // programme from At Apartment above; own backend module (oh_at_home).
    name: 'atHomeGroup',
    displayName: 'At Home',
    icon: { name: 'AtHome' },
    // One left-panel entry per view (owner, 2026-10-03); each page carries
    // Trial / Regular (and Past, on Schedules listed) as tabs on the right.
    subMenus: [
      {
        name: 'atHomeSchedules',
        displayName: 'Schedules listed',
        path: navigationPaths.atHomeSchedules,
        component: 'AtHomeSchedulesPage'
      },
      {
        name: 'atHomeToBook',
        displayName: 'Book a session',
        path: navigationPaths.atHomeToBook,
        component: 'AtHomeToBookPage'
      },
      {
        name: 'atHomeBookings',
        displayName: 'Bookings',
        path: navigationPaths.atHomeBookings,
        component: 'AtHomeBookingsPage'
      },
      {
        name: 'atHomeAttended',
        displayName: 'Attended',
        path: navigationPaths.atHomeAttended,
        component: 'AtHomeAttendedPage'
      },
      {
        name: 'atHomeDashboard',
        displayName: 'Dashboard',
        path: navigationPaths.atHomeDashboard,
        component: 'AtHomeDashboardPage'
      },
      {
        name: 'atHomeIssues',
        displayName: 'Issues',
        path: navigationPaths.atHomeIssues,
        component: 'AtHomeIssuesPage'
      },
      {
        name: 'atHomeSetup',
        displayName: 'Setup',
        path: navigationPaths.atHomeSetup,
        component: 'AtHomeSetupPage'
      }
    ]
  },
  // ACADS — the Kolkata tuition business (Theatre Road + Salt Lake). This is
  // the page the Kolkata team fills. HQ's check and sign-off is a separate
  // page under HQ (hqAcadsReview) so the people who fill cannot see it.
  ...(ACADS_ENABLED
    ? [
        {
          name: 'acads',
          displayName: 'ACADS',
          icon: { name: 'Reports' },
          subMenus: [
            {
              name: 'acadsHome',
              displayName: 'Fees',
              path: navigationPaths.acads,
              component: 'AcadsPage'
            }
          ]
        }
      ]
    : [])
]

export const drawerWidth = 270

export const tableDefaultOption = {
  filter: false,
  print: false,
  viewColumns: false,
  fixedHeader: true,
  fixedSelectColumn: true,
  selectableRows: 'multiple',
  enableNestedDataAccess: '.', // allows nested data separated by ".",
  setTableProps: () => ({
    'data-testid': 'table'
  }),
  search: false,
  downloadOptions: {
    filterOptions: {
      useDisplayedColumnsOnly: true,
      useDisplayedRowsOnly: true
    }
  },
  onDownload: (buildHead, buildBody, columns, data) => {
    if (
      columns[0]?.download?.serverSide === true &&
      columns[0]?.download?.type === 'JM_DASHBOARD'
    ) {
      const page_number = parseInt(localStorage.getItem(constants.PAGE_NO)) + 1
      const page_size = 25
      const jm_name = getJMName()

      if (jm_name && jm_name?.length !== 0) {
        downloadJMDashBoard({
          page_size,
          page_number,
          journey_mentor: jm_name
        })
      } else {
        downloadJMDashBoard({ page_size, page_number })
      }
      return false
    } else {
      const modifiedData = data.map((eachData, rowIndex) => {
        const { data } = eachData

        const modifiedColumnsData = data.map((eachColumnData, columnIndex) => {
          if (
            eachColumnData !== null &&
            (Array.isArray(eachColumnData) ||
              typeof eachColumnData === 'object')
          ) {
            const { customBodyRender, customBodyRenderLite } =
              columns[columnIndex]

            if (typeof customBodyRender === 'function') {
              const element = customBodyRender(eachColumnData)
              return element?.props?.children
            } else if (typeof customBodyRenderLite === 'function') {
              const element = customBodyRenderLite(rowIndex)
              return element?.props?.children
            }
          }

          return eachColumnData
        })

        return {
          ...eachData,
          data: modifiedColumnsData
        }
      })

      return `${buildHead(columns)}${buildBody(modifiedData)}`.trim()
    }
  }
}

export const serverSideOptions = {
  serverSide: true,
  rowsPerPage: 25,
  rowsPerPageOptions: [25]
}

export const readOnlyTableDefaultOption = {
  ...tableDefaultOption,
  selectableRows: 'none'
}

export const tableColumnDefaultOption = {
  options: {
    filter: true,
    sort: true
  }
}

export const getWidgetTypeName = (widgetName) => {
  switch (widgetName) {
    case 'datepicker':
      return 'date'

    case 'timepicker':
      return 'time'

    default:
      return 'text'
  }
}

// cloudinary upload presets for set of folder
// can be found on settings < uploads < upload presets
export const uploadPresets = {
  [constants.USER]: 'user_czawjri1',
  [constants.EVENT_POSTER]: 'event_poster_yrv1y1uz',
  [constants.SUBJECT]: 'subject_eklmdzhh',
  [constants.CLUB]: 'club_r8hq4kks',
  [constants.MOCK_TEST_THUMBNAIL]: 'mock_thumbnails_gqvw7zie',
  [constants.MOCK_TEST_PDF]: 'mock_sample_papers_apsbfg2p',
  [constants.CENTRE]: 'learning-centres',
  [constants.COURSE]: 'course_x12hu6so',
  [constants.SUBJECT_CATEGORY]: 'subject_category_m2ytl7yd',
  [constants.OPENBOOK]: 'openbook_uymcyqzz',
  [constants.ANNOUNCEMENT]: 'announcements'
}

export const commonAttributes = {
  batches: {
    name: 'batches',
    label: 'Batches',
    widget: 'autocomplete',
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        const { uuid, teacher = '', subject = '', timings } = item

        return {
          uuid: uuid,
          name: `${teacher}, ${subject}, ${timings}`
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
      return data.results.map((item) => {
        return getItemStructure(item)
      })
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return data.map((item) => {
        return getItemStructure(item)
      })
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'batches',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      // Lazy. `api/common` and `app/config` sit in an import cycle
      // (api/common -> app/helpers -> api -> api/communication -> app/transformer
      // -> app/config), so whenever api/common is the entry module, `apiEndpoints`
      // is still in its temporal dead zone while this file evaluates and reading
      // it here throws "Cannot read properties of undefined". A getter defers the
      // read to render time, by which point the cycle has settled.
      get url() {
        return apiEndpoints.fetchBatch
      }
    },
    multiple: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'batches',
      customBodyRender: (value) => {
        return (
          <span>
            {Array.isArray(value) &&
              value
                .map((item) => {
                  const { teacher = '', subject = '', timings } = item

                  return `${teacher}, ${subject}, ${timings}`
                })
                .join(' | ')}
          </span>
        )
      }
    }
  },
  boards: {
    name: 'boards',
    label: 'Boards',
    widget: 'autocomplete',
    widgetFilter: true,
    validate: validateNotEmpty,
    optional: true,
    minCharactersToSearch: 0,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid,
          name: item.name
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
      return data.results.map((item) => {
        return getItemStructure(item)
      })
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return data.map((item) => {
        return getItemStructure(item)
      })
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'boards',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return apiEndpoints.board
      }
    },
    multiple: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'boards',
      customBodyRender: (value) => (
        <span>
          {Array.isArray(value) && value.map((item) => item.name).join(', ')}
        </span>
      )
    }
  },
  centre: {
    name: 'centre',
    label: 'Centre',
    widget: 'autocomplete',
    widgetFilter: true,
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return item
          ? {
              uuid: item.uuid,
              name: item.name
            }
          : null
      }

      if (Array.isArray(data)) {
        return data?.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data?.results?.map((item) => {
          return getItemStructure(item)
        })
      }

      return [getItemStructure(data)]
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return getItemStructure(data)
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'centre',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return apiEndpoints.centre
      }
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'centre',
      customBodyRender: (value) => (value ? <span>{value?.name}</span> : null)
    }
  },
  standard: {
    name: 'standards',
    label: 'Standards',
    formDataKey: 'standards',
    widget: 'autocomplete',
    widgetFilter: true,
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid,
          name: item.name
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
        return [getItemStructure(data)]
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data.results.map((item) => {
          return getItemStructure(item)
        })
      } else {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return data.map((item) => {
        return getItemStructure(item)
      })
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'standards',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return apiEndpoints.standard
      }
    },
    multiple: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'standards',
      customBodyRender: (value) => (
        <span>
          {Array.isArray(value) && value.map((item) => item.name).join(', ')}
        </span>
      )
    }
  },
  student: {
    name: 'student.user.name',
    label: 'Student Name',
    widget: 'autocomplete',
    formDataKey: 'student',
    validate: validateNotEmpty,
    minCharactersToSearch: 0,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        if (Object.prototype.hasOwnProperty.call(item, 'user')) {
          return {
            uuid: item?.user?.uuid,
            name: item?.user?.name
              ? item?.user?.name
              : `${item?.user?.first_name} ${item?.user?.last_name}`
          }
        } else {
          return {
            uuid: item.uuid,
            name: item.name
          }
        }
      }
      if (Array.isArray(data) && !isEmpty(data)) {
        return [getItemStructure(data[0])]
      } else if (Object.prototype.hasOwnProperty.call(data, 'user')) {
        return [getItemStructure(data)]
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data.results.map((item) => {
          return getItemStructure(item)
        })
      } else {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        if (Object.prototype.hasOwnProperty.call(item, 'user')) {
          return item.user.uuid
        }
      }
      if (Object.prototype.hasOwnProperty.call(data, 'user')) {
        return getItemStructure(data)
      }
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'student',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return `${apiEndpoints.user}/student`
      }
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'student_name',
      customBodyRender: (value) => (
        <span>
          {value &&
            (value.name
              ? value.name
              : `${value.first_name} ${value.last_name}`)}
        </span>
      )
    }
  },
  subject: {
    name: 'subjects',
    label: 'Subjects',
    formDataKey: 'subjects',
    widget: 'autocomplete',
    widgetFilter: true,
    validate: validateNotEmpty,
    minCharactersToSearch: 0,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid,
          name: item.name
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
        return [getItemStructure(data)]
      }

      return data.results.map((item) => {
        return getItemStructure(item)
      })
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return data.map((item) => {
        return getItemStructure(item)
      })
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'subjects',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return `${apiEndpoints.subject}?limit=600&offset=0`
      }
    },
    multiple: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'subjects',
      customBodyRender: (value) => (
        <span>
          {Array.isArray(value) && value.map((item) => item.name).join(', ')}
        </span>
      )
    }
  },
  club: {
    name: 'club.name',
    label: 'Club',
    formDataKey: 'club',
    widget: 'autocomplete',
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid,
          name: item.name
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
        return [getItemStructure(data)]
      }

      return data.results.map((item) => {
        return getItemStructure(item)
      })
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return getItemStructure(data)
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'club',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return apiEndpoints.club
      }
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'club_name'
    }
  },
  teacher: {
    name: 'teacher.user',
    label: 'Teacher Name',
    widget: 'autocomplete',
    formDataKey: 'teacher',
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        if (Object.prototype.hasOwnProperty.call(item, 'user')) {
          return {
            uuid: item.user.uuid,
            name: `${item.user.first_name} ${item.user.last_name}`
          }
        } else {
          return {
            uuid: item.uuid,
            name: item.name
          }
        }
      }
      if (Array.isArray(data) && !isEmpty(data)) {
        return [getItemStructure(data[0])]
      } else if (Object.prototype.hasOwnProperty.call(data, 'user')) {
        return [getItemStructure(data)]
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data.results.map((item) => {
          return getItemStructure(item)
        })
      } else {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        if (Object.prototype.hasOwnProperty.call(item, 'user')) {
          return item.user.uuid
        }
      }
      if (Object.prototype.hasOwnProperty.call(data, 'user')) {
        return getItemStructure(data)
      }
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'teacher',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return `${apiEndpoints.user}/teacher`
      }
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'teacher_name',
      customBodyRender: (value) => (
        <span>{`${(value && value.first_name) || ''} ${
          (value && value.last_name) || ''
        }`}</span>
      )
    }
  },
  level: {
    name: 'level',
    label: 'Batch Level',
    widget: 'autocomplete',
    widgetFilter: true,
    validate: validateNotEmpty,
    optional: true,
    minCharactersToSearch: 0,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          name: item[1],
          uuid: item[1]
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return Object.entries(data.results[0].value).map((item) =>
          getItemStructure(item)
        )
      }
      return [getItemStructure(data)]
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return getItemStructure(data)
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'level',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      get url() {
        return `${apiEndpoints.configApp}?key=EXCEL_COURSE_LEVELS`
      }
    },
    validate: validateNotEmpty,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'level',
      customBodyRender: (value) => {
        return <div>{levelMap[value]}</div>
      }
    }
  },
  user: {
    name: 'user',
    label: 'User Name',
    widget: 'autocomplete',
    formDataKey: 'user',
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item?.uuid,
          name:
            item?.name || `${item?.first_name || ''} ${item?.last_name || ''}`
        }
      }

      if (Array.isArray(data) && !isEmpty(data)) {
        return [getItemStructure(data[0])]
      } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
        return [getItemStructure(data)]
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data.results.map((item) => {
          return getItemStructure(item)
        })
      } else {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item?.uuid
      }
      return getItemStructure(data)
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'user',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      url: `${apiURLOrigin}/base/user`
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'user_name',
      customBodyRender: (value) => (
        <span>{`${(value && value.first_name) || ''} ${
          (value && value.last_name) || ''
        }`}</span>
      )
    }
  },
  uuid: {
    name: 'uuid',
    label: 'Id',
    disabledInFormOnCreate: true,
    editable: false,
    optional: true,
    options: {
      ...tableColumnDefaultOption.options,
      display: false
    }
  },
  amount: {
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) => {
        return <span>{formatAmount(value)}</span>
      }
    }
  },
  image: {
    name: 'image',
    label: 'Image',
    widget: 'fileupload',
    // optional: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'image',
      disableFilter: true,
      customBodyRender: (value) => (
        <>{!isEmpty(value) ? <Avatar src={value} /> : null}</>
      )
    }
  },
  files: {
    name: 'file',
    label: 'File',
    widget: 'fileupload',
    // optional: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'file',
      disableFilter: true
    }
  },
  date: {
    options: {
      ...tableColumnDefaultOption.options,
      headerWidget: DateRangePicker,
      headerWidgetInputType: 'daterange'
    }
  }
  // month: {
  //   options: {
  //     ...tableColumnDefaultOption.options,
  //     headerWidget: MonthRangePicker,
  //     headerWidgetInputType: 'monthrange'
  //   }
  // }
}

export const indianStatesJson = {
  AN: 'Andaman and Nicobar Islands',
  AP: 'Andhra Pradesh',
  AR: 'Arunachal Pradesh',
  AS: 'Assam',
  BR: 'Bihar',
  CG: 'Chandigarh',
  CH: 'Chhattisgarh',
  DN: 'Dadra and Nagar Haveli',
  DD: 'Daman and Diu',
  DL: 'Delhi',
  GA: 'Goa',
  GJ: 'Gujarat',
  HR: 'Haryana',
  HP: 'Himachal Pradesh',
  JK: 'Jammu and Kashmir',
  JH: 'Jharkhand',
  KA: 'Karnataka',
  KL: 'Kerala',
  LA: 'Ladakh',
  LD: 'Lakshadweep',
  MP: 'Madhya Pradesh',
  MH: 'Maharashtra',
  MN: 'Manipur',
  ML: 'Meghalaya',
  MZ: 'Mizoram',
  NL: 'Nagaland',
  OR: 'Odisha',
  PY: 'Puducherry',
  PB: 'Punjab',
  RJ: 'Rajasthan',
  SK: 'Sikkim',
  TN: 'Tamil Nadu',
  TS: 'Telangana',
  TR: 'Tripura',
  UP: 'Uttar Pradesh',
  UK: 'Uttarakhand',
  WB: 'West Bengal'
}

export const enrolmentTypes = {
  CLASS: 'regular',
  DEMO: 'demo',
  MEMBERSHIP: 'membership',
  EVENT: 'event',
  PROGRAM: 'program',
  MAKE_UP_SESSION: 'make_up_session'
}

export const targetAudienceSelectionModes = {
  AUTOMATIC: 'AUTOMATIC',
  MANUAL: 'MANUAL',
  AUTOMATIC_WITH_EXCLUSION: 'AUTOMATIC_WITH_EXCLUSION'
}

export const bulkMessagingFilterTypes = {
  CENTRE: 'centre',
  TEACHER: 'teacher',
  SUBJECT: 'subject',
  CATEGORY: 'category',
  CLASS: 'class'
}

export const bulkMessagingAvailableFilters = {
  [constants.STUDENT]: [
    bulkMessagingFilterTypes.CENTRE,
    bulkMessagingFilterTypes.TEACHER,
    bulkMessagingFilterTypes.CATEGORY,
    bulkMessagingFilterTypes.SUBJECT
  ],
  [constants.PARENT]: [
    bulkMessagingFilterTypes.CENTRE,
    bulkMessagingFilterTypes.TEACHER,
    bulkMessagingFilterTypes.CATEGORY,
    bulkMessagingFilterTypes.SUBJECT
  ],
  [constants.TEACHER]: [
    bulkMessagingFilterTypes.CENTRE,
    bulkMessagingFilterTypes.SUBJECT,
    bulkMessagingFilterTypes.CATEGORY
  ]
}

export const programList = {
  exporation: 'exploration',
  summer_explore: 'exploration_camp'
}

export const programTypeList = [
  'exploration',
  'exploration_camp',
  'excel',
  'school',
  'care'
]

export const general_summer_explore_batch_list = [
  {
    title: '1st April - 30th April 2024',
    start_date: '2024-04-01',
    end_date: '2024-04-30'
  },
  {
    title: '1st May - 28th May 2024',
    start_date: '2024-05-01',
    end_date: '2024-05-28'
  }
]

//For TR & SL
export const special_summer_explore_batch_list = [
  {
    title: '14th May - 24th May',
    start_date: '2024-05-14',
    end_date: '2024-05-24'
  }
]

export const programsMap = ['exploration', 'exploration_camp']
export const countList = {
  1: '1',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10'
}

export const makeUpRadioButtons = [
  {
    value: 'cancelled',
    control: <Radio />,
    label: 'Cancelled Session'
  },
  {
    value: 'no-show',
    control: <Radio />,
    label: 'No Show '
  }
]
export const levelMap = {
  1: 'beginner',
  2: 'intermediate',
  3: 'advanced'
}

export const batchTypesMap = {
  class: 'class',
  explore: 'explore',
  // membership: 'regular member',
  event: 'event',
  excel: 'excel',
  exploration: 'exploration',
  exploration_camp: 'exploration_camp',
  school: 'preschool',
  care: 'care'
}

export const statusMap = {
  active: 'active',
  terminated: 'terminated',
  lapsed: 'lapsed',
  upcoming: 'upcoming',
  due: 'due'
}

export const sourceMap = {
  member_app: 'Member App',
  alfred: 'Alfred'
}

export const ageRange = {
  min: 3,
  max: 18
}

export const cityCodes = {
  Bengaluru: 'Bengaluru',
  Kolkata: 'Kolkata'
}

export const platforms = {
  oh_meets: 'OH Meets',
  zoom: 'Zoom'
}

export const yesNoOptions = {
  true: 'yes',
  false: 'no'
}

export const attendanceMap = {
  true: 'Present',
  false: 'Absent'
}

export const batchTypes = {
  class: 'regular non-member',
  event: 'event',
  mock: 'mock',
  explore: 'trial',
  membership: 'regular member',
  excel: 'excel',
  exploration: 'exploration'
}

export const invoiceTypes = {
  newMember: 'new member',
  renewal: 'renewal',
  topUp: 'add coins'
}

export const centreScheduleBatchTypes = {
  class: 'regular',
  event: 'workshop',
  mock: 'mock',
  explore: 'trial',
  membership: 'regular',
  exploration: 'exploration',
  exploration_camp: 'summer'
}

export const subjectCategoryTypes = {
  academic: 'academic',
  extracurricular: 'extracurricular'
}

export const studentSourceTypes = {
  teacher: 'teacher',
  openhouse: 'openhouse'
}

export const teacherStatuses = {
  active: 'Active',
  applied: 'Applied',
  terminated: 'Terminated'
}

export const teacherTypes = {
  superprovider: 'superprovider',
  independent: 'independent'
}

export const terminationReasons = {
  price: 'Price',
  teacher: 'Teacher',
  openhouse: 'Openhouse'
}

export const statuses = {
  active: 'Active',
  suspended: 'Suspended'
}

export const app_statuses = {
  active: 'Active',
  inactive: 'Inactive',
  canceled: 'Cancelled'
}

export const months = {
  jan: 'January',
  feb: 'February',
  mar: 'March',
  apr: 'April',
  may: 'May',
  jun: 'June',
  jul: 'July',
  aug: 'August',
  sep: 'September',
  oct: 'October',
  nov: 'November',
  dec: 'December'
}

export const prorataModes = {
  absolute: 'absolute',
  percentage: 'percentage'
}

export const START_DATE_MAX_NUMBER = 8

export const paymentModes = {
  upi: 'UPI',
  cash: 'Cash',
  bank: 'Bank',
  cheque: 'Cheque',
  razorpay_manual_link: 'Razorpay Manual Link',
  pos_machine: 'POS Machine',
  paid_to_teacher: 'Paid To Teacher',
  payment_gateway_error: 'Payment Gateway Error',
  proeves_parent: 'ProEves Parent'
}

export const reasons = {
  topUp: 'Recharge',
  promotion: 'Promotion',
  refund: 'Refund'
}

export const invoiceActions = {
  remind: 'Remind',
  edit: 'Edit',
  collect: 'Collect',
  cancel: 'Cancel'
}

export const independantTeacherActions = {
  edit: 'Edit',
  approve: 'Approve',
  unapprove: 'Unapprove',
  dispatch: 'Dispatch'
}

export const parentActions = {
  // extend: 'Extend',
  topup: 'Add coins',
  deduct: 'Deduct',
  setup_emandate: 'Setup e-Mandate',
  enrolment: 'Register',
  // renew_now: 'Renew Early',
  // change_plan: 'Change Plan',
  // cancel: 'Cancel Membership',
  // suggest_schedule: 'Suggest Schedule',
  change_phone_number: 'Change Phone Number'
}

// Shown only to Super Admins, slotted in right after the coin actions (Add coins /
// Deduct) on the parent dashboard. The backend rejects anyone else as well.
export const superAdminWalletActions = {
  extend_wallet_validity: 'Extend Wallet Validity'
}

export const nonMemberActions = {
  // suggest_schedule: 'Suggest Schedule',
  change_phone_number: 'Change Phone Number',
  topup: 'Add coins',
  // deduct: 'Deduct',
  enrolment: 'Register'
}

export const memberActions = {
  remind: 'Remind',
  collect: 'Collect',
  cancel: 'Cancel'
}

export const topupActions = {
  // remind: 'Remind',
  collect: 'Collect',
  cancel: 'Cancel'
}

export const renewalActions = {
  remind: 'Remind',
  collect: 'Collect',
  cancel: 'Cancel'
}

export const cancelActions = {
  terminate: 'Terminate',
  pause: 'Hibernate'
}

export const excelEnrolmentActions = {
  // Program-native actions on Excel (ProgramEnrolment) enrolments. Pause +
  // Change Timings are served by /oh_programs/enrolment/excel/* (not the
  // subscription FSM). Terminate + Cancel Sessions use the existing
  // program-level / per-session endpoints.
  pause_enrolment: 'Pause',
  resume_enrolment: 'Resume',
  terminate_enrolment: 'Terminate',
  // Super-admin only, and filtered out for everyone else in ExcelEnrolment.js.
  // Terminate is whole-month and forward-dated; this one cuts the student off
  // today, mid-month, with no refund (settled manually).
  terminate_immediately: 'Terminate Immediately',
  change_timings: 'Change Timings',
  cancel_sessions: 'Cancel Sessions'
}

// Batch types that should expose only a Terminate action (Event, Trial/Drop-in).
export const terminateOnlyActions = {
  terminate_enrolment: 'Terminate'
}

// Explore (exploration program) enrolments — per-session cancellation only.
export const exploreEnrolmentActions = {
  cancel_sessions: 'Cancel Sessions'
}

export const cancellationActions = {
  cancel: 'Cancel'
}

export const programActions = {
  terminate_enrolment: 'Terminate',
  // Feature 2 — mid-term programme upgrade. The modal handles programme /
  // standard / batch change + effective date + downgrade-override. Visible
  // for ACTIVE SCHOOL admissions only; PSACEnrolment.js gates the render.
  modify_admission: 'Modify admission',
  // Phase 3 (Akshay 2026-05-27) — cancel an awaiting_payment
  // PendingAdmissionModification. Hidden on rows with no pending
  // upgrade in flight; replaces the modify_admission button while a
  // pending upgrade is awaiting payment.
  cancel_pending_modification: 'Cancel pending upgrade',
  // Feature 5 — AIM-only seat block actions. Both visible only on
  // seat-blocked admissions (Term 1 PENDING + AIM paid).
  generate_term_invoice: 'Generate Term Fee Invoice',
  release_seat: 'Release Seat'
}

export const toddlerClubEnrolmentActions = {
  pause_enrolment: 'Pause',
  terminate_enrolment: 'Terminate'
}

export const OPENDAY_SLOTS_MINUTE_INTERVAL = 15

export const userStatusOptions = {
  true: 'customer',
  false: 'non-customer'
}

export const sourceTypes = {
  alfred: 'alfred',
  member_app: 'member app',
  website: 'website'
}

export const cartTypes = {
  topup: 'Topup',
  booking: 'Booking',
  topup_booking: 'Booking & Recharge'
}

export const cartStatusTypes = {
  pending: 'Pending',
  completed: 'Completed',
  booking_error: 'Booking Error',
  expired: 'Expired'
}

export const quillSanitizeWhitelist = {
  allowedTags: [
    'a',
    'p',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'b',
    'i',
    'u',
    's',
    'em',
    'strong',
    'strike',
    'sub',
    'sup',
    'hr',
    'br',
    'blockquote',
    'caption',
    'code',
    'pre',
    'col',
    'colgroup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'img'
  ],
  allowedAttributes: {
    a: ['href', 'name', 'target', 'rel'],
    span: ['style'],
    p: ['class'],
    li: ['class'],
    pre: ['class'],
    img: ['src', 'alt', 'width', 'height']
  },
  allowedStyles: {
    '*': {
      'background-color': [
        /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/
      ],
      color: [/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/]
    }
  },
  allowedClasses: {
    '*': [
      'ql-align-right',
      'ql-align-center',
      'ql-align-justify',
      'ql-code-block',
      'ql-code-block-container',
      'ql-syntax',
      'ql-direction-rtl',
      'ql-font-serif',
      'ql-font-monospace',
      'ql-formula',
      'ql-indent-1',
      'ql-indent-2',
      'ql-indent-3',
      'ql-indent-4',
      'ql-indent-5',
      'ql-indent-6',
      'ql-indent-7',
      'ql-indent-8',
      'ql-size-small',
      'ql-size-large',
      'ql-size-huge'
    ]
  }
}

export const user_statuses = {
  new: 'New',
  customer: 'Customer',
  trial_booked: 'Trial Booked'
}
